import {
  Archive as ArchiveIcon,
  DeleteOutline as DeleteIcon,
  Book as BookIcon,
  Edit as EditIcon,
  Sync as SyncIcon
} from '@mui/icons-material';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { NarrativeSummary } from '@/lib/types';

interface ChapterColumnMenuProps {
  chapter: NarrativeSummary;
  menuAnchor: HTMLElement | null;
  onMenuClose: () => void;
  onRename: () => void;
  onArchive: () => void;
  onDelete: () => void;
  onUpdateStatus: (status: 'draft' | 'preview' | 'public') => void;
  onAssemble: () => void;
  hasScenes: boolean;
  canPublish: boolean;
}

export function ChapterColumnMenu({
  chapter,
  menuAnchor,
  onMenuClose,
  onRename,
  onArchive,
  onDelete,
  onUpdateStatus,
  onAssemble,
  hasScenes,
  canPublish
}: ChapterColumnMenuProps) {
  const { t } = useTranslation();
  const open = Boolean(menuAnchor);
  const hasCompiledVersion = !!chapter.mainContentId;

  return (
    <Menu
      anchorEl={menuAnchor}
      open={open}
      onClose={onMenuClose}
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          boxShadow: 8,
          borderRadius: 2,
          minWidth: 180,
          mt: 1,
          border: '1px solid',
          borderColor: 'divider'
        }
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      {/* Rename action */}
      <MenuItem
        onClick={() => {
          onMenuClose();
          setTimeout(() => {
            onRename();
          }, 100);
        }}
        sx={{ gap: 1.5, py: 1, fontSize: '0.8rem', fontWeight: 700 }}
      >
        <EditIcon sx={{ fontSize: 16, opacity: 0.7 }} />
        {t('common.actions.rename')}
      </MenuItem>

      <Divider sx={{ my: 0.5, borderColor: 'divider' }} />

      {/* Final Edition / Assemble actions */}
      {!hasCompiledVersion ? (
        <MenuItem
          disabled={!hasScenes}
          onClick={() => {
            onAssemble();
            onMenuClose();
          }}
          sx={{ gap: 1.5, py: 1, fontSize: '0.8rem', fontWeight: 700 }}
        >
          <BookIcon sx={{ fontSize: 16, opacity: 0.7 }} />
          {t('chapters.publish.assemble_action')}
        </MenuItem>
      ) : (
        <>
          <MenuItem
            component={Link}
            to={`/publish-editor/${chapter.id}`}
            onClick={onMenuClose}
            sx={{ gap: 1.5, py: 1, fontSize: '0.8rem', fontWeight: 700 }}
          >
            <EditIcon sx={{ fontSize: 16, opacity: 0.7 }} />
            {t('chapters.publish.edit_final_label')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              onAssemble();
              onMenuClose();
            }}
            sx={{ gap: 1.5, py: 1, fontSize: '0.8rem', fontWeight: 700 }}
          >
            <SyncIcon sx={{ fontSize: 16, opacity: 0.7 }} />
            {t('chapters.publish.re_assemble_label')}
          </MenuItem>
        </>
      )}

      <Divider sx={{ my: 0.5, borderColor: 'divider' }} />

      {/* Set status shortcuts */}
      <MenuItem
        onClick={() => {
          onUpdateStatus('draft');
          onMenuClose();
        }}
        disabled={chapter.publishStatus === 'draft'}
        sx={{ gap: 1.5, py: 1, fontSize: '0.8rem', fontWeight: 500 }}
      >
        {t('chapters.publish.set_draft')}
      </MenuItem>
      <MenuItem
        onClick={() => {
          onUpdateStatus('preview');
          onMenuClose();
        }}
        disabled={chapter.publishStatus === 'preview'}
        sx={{ gap: 1.5, py: 1, fontSize: '0.8rem', fontWeight: 500 }}
      >
        {t('chapters.publish.set_preview')}
      </MenuItem>
      <Tooltip title={!canPublish ? t('chapters.publish.owner_only') : ''} placement={'right'}>
        <MenuItem
          onClick={() => {
            onUpdateStatus('public');
            onMenuClose();
          }}
          disabled={!canPublish || chapter.publishStatus === 'public'}
          sx={{ gap: 1.5, py: 1, fontSize: '0.8rem', fontWeight: 500, ...(!canPublish ? { pointerEvents: 'auto' } : {}) }}
        >
          {t('chapters.publish.set_public')}
        </MenuItem>
      </Tooltip>

      <Divider sx={{ my: 0.5, borderColor: 'divider' }} />

      {/* Archive / Delete */}
      <MenuItem
        onClick={() => {
          onArchive();
          onMenuClose();
        }}
        sx={{ gap: 1.5, py: 1, fontSize: '0.8rem', fontWeight: 700 }}
      >
        <ArchiveIcon sx={{ fontSize: 16, opacity: 0.7 }} />
        {t('common.actions.archive')}
      </MenuItem>
      <MenuItem
        onClick={() => {
          onDelete();
          onMenuClose();
        }}
        sx={{ gap: 1.5, py: 1, fontSize: '0.8rem', fontWeight: 700, color: 'error.main' }}
      >
        <DeleteIcon sx={{ fontSize: 16, opacity: 0.7 }} />
        {t('common.actions.delete')}
      </MenuItem>
    </Menu>
  );
}
